import { Injectable, signal } from '@angular/core';
import axios, { AxiosInstance } from 'axios';
import { ChatMessage } from '../chat-message.model';
import { environment } from '../../../../environments/environment';
import { ProductAdvisorApiResponse } from '../product-advisor-api-response.model';
import { UserService } from '@app/services';
import { filter, first } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class ProductAdvisorService {
  private readonly client: AxiosInstance;

  private readonly roomPlanerApi = environment.productAdvisor.apiUrl;

  private conversationId: string = undefined;
  private exampleRoomIds: string[] = [];
  private pastSearchRequests: string[] = [];
  private products: string[] = [];
  private userRequestedProducts: string[] = [];
  private chatHistory: ProductAdvisorApiResponse[] = [];
  private roomDims: number[] = [5, 5];
  private user = {
    email_address: '',
    id: 'anonymous',
  };
  showContactForm = signal<boolean>(false);

  constructor(private userService: UserService) {
    this.client = axios.create({
      baseURL: this.roomPlanerApi,
      auth: {
        username: environment.productAdvisor.username,
        password: environment.productAdvisor.password,
      },
    });
    this.initUser();
  }

  initUser() {
    this.userService
      .getCurrentUser()
      .pipe(
        filter((user) => Boolean(user)),
        first()
      )
      .subscribe(async (user) => {
        this.user = {
          id: user.uid,
          email_address: user.email,
        };
        await this.setUser(this.user.id, this.user.email_address);
      });
  }

  getChatHistory() {
    return this.chatHistory;
  }

  async requestWelcomeMessage() {
    return await this.client.get(`${this.roomPlanerApi}/welcome-message`);
  }

  async setUser(id: string, emailAddress: string) {
    return await this.client.post(`${this.roomPlanerApi}/update-user`, {
      conversation_id: this.conversationId,
      user: {
        id: id,
        email_address: emailAddress,
      },
    });
  }

  async sendMessage(message: ChatMessage) {
    this.chatHistory.push({
      id: null,
      content: message.content as string,
      recommendations: null,
      role: 'user',
      timestamp: message.timestamp.toISOString(),
      explicit_human_advisor_requested: false,
    });

    const response = await this.client.post(`${this.roomPlanerApi}/message`, {
      message_history: this.chatHistory,
      payload: {
        conversationId: this.conversationId,
        example_room_ids: this.exampleRoomIds,
        past_search_requests: this.pastSearchRequests,
        products: this.products,
        user_requests_products: this.userRequestedProducts,
      },
      room_dims: this.roomDims,
      user: this.user,
    });

    if (response.status === 200) {
      if (!this.conversationId && response.data.payload.conversation_id) {
        this.conversationId = response.data.payload.conversation_id;
      }
      this.chatHistory = response.data.message_history;
      this.pastSearchRequests = response.data.payload.past_search_requests;
      this.exampleRoomIds = response.data.payload.example_room_ids;
      this.products = response.data.payload.products;
      this.userRequestedProducts = response.data.payload.user_requested_products;

      const lastMessage = response.data.message_history[response.data.message_history.length - 1];
      if (lastMessage?.explicit_human_advisor_requested) {
        this.showContactForm.set(true);
      }

      return response;
    } else {
      console.error(`An error occurred while sending message: ${JSON.stringify(response)}`);
      return null;
    }
  }

  public resetConversation() {
    this.conversationId = null;
    this.chatHistory = [];
    this.showContactForm.set(false);
    this.initUser();
  }

  public setRoomX(x: number) {
    this.roomDims = [x, this.roomDims[1]];
  }

  public setRoomY(y: number) {
    this.roomDims = [this.roomDims[0], y];
  }

  public setRoomDims(x: number, y: number) {
    return (this.roomDims = [x, y]);
  }

  public getDims() {
    return this.roomDims;
  }
}
